// Types
import {Category, CurrentSelectedCategory} from "interfaces/category";
import { FiltersInterface } from "interfaces/osaSearch";

// Constants
import {ReturnFields} from "constants/gpoSearchEnum";

// Services
import {commonOsaQueryOnlyTags} from "services/osaSearch/osaSearch";

// Utils
import { getNextUrl } from "utils/queryString";

export const handleInvalidTags = (
    sortedSelectedFilters: Category[],
    availableFilters: Category[],
    pathname: string,
    selectedCategories: CurrentSelectedCategory[],
    allCategories: Category[]
) => {

    const availableFilterIds = new Set(availableFilters.map(filter => filter.id));

    const filteredFilters = sortedSelectedFilters.filter(tag => availableFilterIds.has(tag.id));

    if (filteredFilters.length === sortedSelectedFilters.length) {
        return { newSelectedCategories: selectedCategories, newSortedSelectedFilters: sortedSelectedFilters, newPathname: pathname, hasInvalidTags: false};
    }

    const tagsToRemove = sortedSelectedFilters.filter(tag => !availableFilterIds.has(tag.id))
    const newSelectedCategories = tagsToRemove.reduce(
        (categories, tagToRemove) => removeTagFromCategories(categories, tagToRemove.id),
        selectedCategories);

    const newPathname = newSelectedCategories.reduce(
        (accPathname, category) => getNextUrl(category.macroCategory, category.selectedTags, accPathname, allCategories),
        pathname);

    return { newSelectedCategories , newSortedSelectedFilters: filteredFilters, newPathname: newPathname, hasInvalidTags: true};
};

export const fetchAvailableFiltersLastInteraction = async (
    osaContext: any,
    activeFilters: FiltersInterface[],
    allCategories: Category[],
    querySearch: string,
    lastCategorySelected: Category | null,
    availableFilters: Category[],
    lastFilterAction: "added" | "removed" | null,
    previousAvailableFilters?: Category[]
): Promise<Category[]> => {

    const getAvailableFilters = async (filters: FiltersInterface[]): Promise<Category[]> => {
        const results = await commonOsaQueryOnlyTags(osaContext, filters, allCategories, querySearch);
        return results ? results.tags : [];
    };

    const filtersSet = new Map(availableFilters.map(filter => [filter.id, filter]));

    const handleFilterAddition = (previousFilters: Category[]): void => {
        if (!lastCategorySelected || !previousFilters?.length) return;

        previousFilters.forEach(filter => {
            if (filter.urlParameter === lastCategorySelected.urlParameter && !filtersSet.has(filter.id)) {
                filtersSet.set(filter.id, filter);
            }
        });
    };

    const handleFilterRemoval = async (): Promise<void> => {
        if (!lastCategorySelected) return;

        const updatedActiveFilters = activeFilters.map(filter => ({
            ...filter,
            terms: filter.fieldName === ReturnFields.TAG_ID
                ? filter.terms.filter(term => term !== lastCategorySelected.id.toString())
                : filter.terms
        }));

        const previousAvailableFiltersForDeselection = await getAvailableFilters(updatedActiveFilters);
        previousAvailableFiltersForDeselection.forEach(filter => {
            if (filter.urlParameter === lastCategorySelected.urlParameter && !filtersSet.has(filter.id)) {
                filtersSet.set(filter.id, filter);
            }
        });
    };

    if (lastFilterAction === "added" && lastCategorySelected && previousAvailableFilters) {
        handleFilterAddition(previousAvailableFilters);
    } else if (lastFilterAction === "removed" && lastCategorySelected) {
        await handleFilterRemoval();
    }

    return Array.from(filtersSet.values());
};

const removeTagFromCategories = (categories: CurrentSelectedCategory[], tagId: number): CurrentSelectedCategory[] => {
    return categories
        .map(category => ({
            ...category,
            selectedTags: category.selectedTags.filter(tag => tag.id !== tagId)
        }))
        .filter(category => category.selectedTags.length > 0);
};
